// src/Components/Wizard/matchers.js
// ===== Normalized（featuresNormalized）専用 =====

export const mountTypeNormMap = {
  屋内: ["indoor", "both"],
  屋外: ["outdoor", "both", "salt"],
  塩害地域: ["salt"],
  屋内外両用: ["both"],
  "特殊用途（ATM・車載など）": ["special"],
  こだわらない: ["indoor", "outdoor", "both", "salt", "special", "unknown"],
};

export const connectivityNormMap = {
  LANケーブル: ["wired_lan", "poe"],
  "Wi-Fi": ["wifi"],
  "LTE/4G": ["lte_4g"],
  PoE: ["poe"],
  アナログ: ["analog"],
  こだわらない: ["wired_lan", "wifi", "lte_4g", "poe", "analog", "unknown"],
};

export const recordingNormMap = {
  SDカード: ["sd"],
  クラウド: ["cloud"],
  "NAS / NVR": ["nas", "nvr"],
  "DVR（同軸）": ["dvr"],
  こだわらない: ["sd", "cloud", "nas", "nvr", "dvr", "unknown"],
};

export const lensTypeNormMap = {
  広い範囲を撮りたい: ["ultraWide", "wide", "fisheye"],
  人や顔をちゃんと映したい: ["standard"],
  ズームして遠くを見たい: ["tele", "zoom", "ptz"],
  こだわらない: ["ultraWide", "wide", "standard", "tele", "fisheye", "ptz", "unknown"],
};

// 暗所：ライト内蔵とみなすタグ（data/dict/lowlight_patterns.json のタグ名）
export const LOW_LIGHT_BUILT_IN_TAGS = ["ir_led", "white_light", "ir_distance", "smart_ir"];

export const resolutionRuleMap = {
  はっきり: (mp) => mp >= 8,
  普通: (mp) => mp >= 5 && mp < 8,
  最低限: (mp) => mp > 0 && mp < 5,
  こだわらない: () => true,
};

export const featureMatches = (_productValue, selectedValue, key, product) => {
  if (selectedValue === "こだわらない") return true;

  const norm = product?.featuresNormalized;
  if (!norm) return false;

  switch (key) {
    // 建物の種類は商品スペックと関係しないため、常に一致扱い（相談時の参考情報）
    case "buildingType":
      return true;

    case "mountType":
      return mountTypeNormMap[selectedValue]?.includes(norm.mountType) ?? false;

    case "connectivity": {
      const allow = connectivityNormMap[selectedValue] ?? [];
      const tags = Array.isArray(norm.connectivity) ? norm.connectivity : [];
      return allow.some((t) => tags.includes(t));
    }

    case "recordingMethod": {
      const allow = recordingNormMap[selectedValue] ?? [];
      const tags = Array.isArray(norm.recordingMethod) ? norm.recordingMethod : [];
      return allow.some((t) => tags.includes(t));
    }

    case "lensType": {
      const allow = lensTypeNormMap[selectedValue] ?? [];
      // lensTags（複数）があれば優先。無ければ旧 lensType で判定
      const tags = Array.isArray(norm.lensTags) ? norm.lensTags : [norm.lensType];
      return allow.some((t) => tags.includes(t));
    }

    case "waterproofDustproof": {
      const hasIp = norm.ipRating != null;
      if (selectedValue === "有り") return hasIp;
      if (selectedValue === "無し") return !hasIp;
      return false;
    }

    case "resolution": {
      const rule = resolutionRuleMap[selectedValue];
      if (!rule) return false;
      if (typeof norm.resolutionMp !== "number") return false;
      return rule(norm.resolutionMp);
    }

    case "lowLightPerformance": {
      const tags = Array.isArray(norm.lowLightTags) ? norm.lowLightTags : [];
      // ライト（赤外線・白色LED）内蔵：照明のない場所でも撮影できる
      if (selectedValue === "真っ暗でも映したい") {
        return LOW_LIGHT_BUILT_IN_TAGS.some((t) => tags.includes(t));
      }
      // ライト内蔵に加えて、最低照度の記載がある高感度機種も含む
      if (selectedValue === "薄暗い程度で映ればいい") return tags.length > 0;
      // 旧選択肢（2026-10以前の回答との互換用）
      if (selectedValue === "有り") return tags.length > 0;
      if (selectedValue === "無し") return tags.length === 0;
      return false;
    }

    case "aiFeatures": {
      const tags = Array.isArray(norm.aiTags) ? norm.aiTags : [];
      if (selectedValue === "有り") return tags.length > 0;
      if (selectedValue === "無し") return tags.length === 0;
      return false;
    }

    default:
      return false;
  }
};
